import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardBody,
  CardHeader,
  Badge,
  Row,
  Col,
  Spinner,
} from 'reactstrap';
import { withTranslation } from 'react-i18next';
import { get } from '../../helpers/api_helper';

function PickPaceLeaderboard(props) {
  const { t, offset } = props;
  const [boards, setBoards] = useState({
    day: { loading: true, results: [] },
    week: { loading: true, results: [] },
    month: { loading: true, results: [] },
  });

  const periods = [
    { key: 'day', label: t('Day') },
    { key: 'week', label: t('Week') },
    { key: 'month', label: t('Month') },
  ];

  const formatSeconds = (seconds) => {
    if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) {
      return '--:--';
    }

    const totalSeconds = Math.max(0, Number(seconds));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setBoards({
        day: { loading: true, results: [] },
        week: { loading: true, results: [] },
        month: { loading: true, results: [] },
      });

      const entries = await Promise.all(periods.map(async (period) => {
        try {
          const response = await get(`/api/warehouse/report/top-pick-pace/${period.key}/${offset}?page=1&sort=pick_time_secs&dir=asc&per=10&f=`);
          return [period.key, { loading: false, results: response.results || [] }];
        } catch (error) {
          return [period.key, { loading: false, results: [], error: error.message }];
        }
      }));

      if (mounted) {
        setBoards(Object.fromEntries(entries));
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [offset]);

  return (
    <Card
      className="pick-pace-leaderboard border-0 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #101828 0%, #1d2939 45%, #b42318 100%)',
        color: '#fff',
        boxShadow: '0 18px 40px rgba(16, 24, 40, 0.28)',
      }}
    >
      <CardHeader
        className="border-0"
        style={{
          background: 'rgba(255, 255, 255, 0.08)',
        }}
      >
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <div>
            <div className="text-uppercase small opacity-75" style={{ letterSpacing: '0.12em' }}>{t('Race Pace')}</div>
            <h5 className="mb-1 text-white">{t('Pick Pace Leaderboard')}</h5>
            <div className="small opacity-75">{t('Lowest average pick time seconds across completed pickroutes.')}</div>
          </div>
          <Badge color="warning" pill className="text-dark align-self-center">
            {t('Leaderboard')}
          </Badge>
        </div>
      </CardHeader>
      <CardBody
        style={{
          backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.06) 0, rgba(255,255,255,0.06) 12px, transparent 12px, transparent 24px)',
        }}
      >
        <Row className="g-3">
          {periods.map((period) => {
            const board = boards[period.key];

            return (
              <Col lg="4" key={period.key}>
                <div
                  className="pick-pace-leaderboard__panel rounded h-100 p-3"
                  style={{
                    background: 'rgba(0, 0, 0, 0.28)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    backdropFilter: 'blur(2px)',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="text-uppercase small opacity-75">{period.label}</div>
                    <span className="small opacity-75">{t('Top 10')}</span>
                  </div>

                  {board.loading && (
                    <div className="text-center py-4">
                      <Spinner color="light" />
                    </div>
                  )}

                  {!board.loading && board.results.length === 0 && (
                    <div className="small opacity-75 py-4 text-center">{t('No Results.')}</div>
                  )}

                  {!board.loading && board.results.length > 0 && (
                    <div className="pick-pace-leaderboard__table-wrap table-responsive">
                      <table className="pick-pace-leaderboard__table table table-sm mb-0 text-white align-middle">
                        <thead>
                          <tr style={{ borderColor: 'rgba(255,255,255,0.12)' }}>
                            <th className="border-0 ps-0">#</th>
                            <th className="border-0">{t('Name')}</th>
                            <th className="border-0 text-end">{t('Pick Time')}</th>
                            <th className="border-0 text-end pe-0">{t('Routes')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {board.results.map((entry, index) => (
                            <tr key={`${period.key}_${entry.user_name}`} style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                              <td className="ps-0 fw-bold">{index + 1}</td>
                              <td>
                                <div className="fw-semibold">{entry.user_name || '-'}</div>
                                <div className="small opacity-75">{`${entry.stops || 0} ${t('stops')}`}</div>
                              </td>
                              <td className="text-end">
                                <div className="fw-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {formatSeconds(entry.pick_time_secs)}
                                </div>
                                <div className="small opacity-75">{`${entry.pick_time_secs || 0}s`}</div>
                              </td>
                              <td className="text-end pe-0 fw-semibold">{entry.routes || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </Col>
            );
          })}
        </Row>
      </CardBody>
    </Card>
  );
}

PickPaceLeaderboard.defaultProps = {
  t: (x) => x,
};

PickPaceLeaderboard.propTypes = {
  offset: PropTypes.number.isRequired,
  t: PropTypes.any,
};

export default withTranslation()(PickPaceLeaderboard);
